import React, { useState, useEffect, useRef } from "react";

import {
  Droplets,
  Fish,
  Wind,
  Factory,
  AudioLines,
  Mountain,
  ArrowRight,
} from "lucide-react";

import WaterService from "./services/waterservice";
import HydrobiologyService from "./services/hydrobiology-service";
import AirService from "./services/airservice";
import FixedSourcesService from "./services/fixed-sources-service";
import NoiseService from "./services/noiseservice";
import SoilService from "./services/soilservice";

const Monitoring = () => {
  const heroImage =
    "/Images/services/enviromental/monitoring/monitoreo.webp";

  const services = [
    {
      id: "agua",
      title: "Calidad de Agua",
      icon: Droplets,
    },
    {
      id: "hidrobiologia",
      title: "Hidrobiología",
      icon: Fish,
    },
    {
      id: "aire",
      title: "Calidad de Aire",
      icon: Wind,
    },
    {
      id: "fijas",
      title: "Fuentes Fijas",
      icon: Factory,
    },
    {
      id: "ruido",
      title: "Ruido",
      icon: AudioLines,
    },
    {
      id: "suelos",
      title: "Suelos",
      icon: Mountain,
    },
  ];

  const [activeService, setActiveService] = useState("agua");

  const serviceContentRef = useRef(null);

  useEffect(() => {
  if (!serviceContentRef.current) return;

  const stickyMenuHeight = 200;

  const elementPosition =
    serviceContentRef.current.getBoundingClientRect().top;

  const offsetPosition =
    elementPosition + window.scrollY - stickyMenuHeight;

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });
}, [activeService]);

  const renderService = () => {
    switch (activeService) {
      case "agua":
        return <WaterService />;

      case "hidrobiologia":
        return <HydrobiologyService />;

      case "aire":
        return <AirService />;

      case "fijas":
        return <FixedSourcesService />;

      case "ruido":
        return <NoiseService />;

      case "suelos":
        return <SoilService />;

      default:
        return <WaterService />;
    }
  };

  return (
    <section className="relative">
      <div className="relative h-[430px] sm:h-[560px] md:h-[640px] lg:h-[680px] overflow-hidden">
        <img
          src={heroImage}
          alt="Monitoreo Ambiental"
          className="absolute inset-0 w-full h-full object-cover object-[70%_center] sm:object-center"
        />

        <div className="absolute inset-0 bg-black/35" />

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#ee7d32]/20 text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-wider">
                Servicios
              </span>

              <h1 className="mt-4 sm:mt-6 text-3xl sm:text-4xl md:text-6xl xl:text-7xl font-bold text-white leading-[0.95]">
                Monitoreo <br />
                <span className="text-[#ee7d32]">Ambiental</span>
              </h1>

              <p className="mt-4 sm:mt-6 text-white/90 text-sm sm:text-base md:text-lg leading-6 sm:leading-7 md:leading-8 max-w-2xl">
                Soluciones integrales en medición, análisis y cumplimiento
                normativo para proteger el ambiente y asegurar la
                sostenibilidad.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-5">
          <div className="mb-3 sm:mb-4">
            <span className="text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-wider">
              Nuestros Servicios
            </span>

            <h2 className="mt-2 text-xl sm:text-2xl md:text-3xl font-bold text-[#111827]">
              Áreas de Monitoreo Ambiental
            </h2>
          </div>

          <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5 sm:gap-3">
            {services.map((service) => {
              const Icon = service.icon;
              const isActive = activeService === service.id;

              return (
                <button
                  key={service.id}
                  onClick={() => setActiveService(service.id)}
                  className={`group relative overflow-hidden rounded-xl border px-3 py-3 transition-all duration-300 ${
                    isActive
                      ? "bg-white border-[#ee7d32] shadow-[0_6px_20px_rgba(238,125,50,0.10)]"
                      : "bg-white border-[#ececec] hover:border-[#ee7d32]/20 hover:-translate-y-0.5 hover:shadow-md"
                  }`}
                >
                  <div
                    className={`absolute top-0 left-0 h-0.5 w-full transition-all duration-300 ${
                      isActive
                        ? "bg-[#ee7d32]"
                        : "bg-transparent group-hover:bg-[#ee7d32]/30"
                    }`}
                  />

                  <div className="flex justify-center">
                    <Icon
                      className="w-6 h-6 transition-all duration-300"
                      style={{
                        stroke: isActive ? "#ee7d32" : "#9ca3af",
                        fill: "none",
                        transform: isActive
                          ? "scale(1.08)"
                          : "scale(1)",
                      }}
                      strokeWidth={1.8}
                    />
                  </div>

                  <div className="mt-2 min-h-[36px] flex items-center justify-center">
                    <h3
                      className={`text-xs font-semibold leading-snug text-center transition-colors duration-300 ${
                        isActive
                          ? "text-[#111827]"
                          : "text-[#374151]"
                      }`}
                    >
                      {service.title}
                    </h3>
                  </div>

                  <div className="mt-2 flex justify-center">
                    <div
                      className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ${
                        isActive
                          ? "bg-[#ee7d32]/10"
                          : "bg-[#f3f4f6] group-hover:bg-[#ee7d32]/10"
                      }`}
                    >
                      <ArrowRight
                        className="w-4 h-4 transition-all duration-300"
                        style={{
                          stroke: isActive
                            ? "#ee7d32"
                            : "#9ca3af",
                          fill: "none",
                          transform: isActive
                            ? "rotate(90deg)"
                            : "translateX(0)",
                        }}
                        strokeWidth={2}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="sm:hidden grid grid-cols-3 gap-2 mt-4">
            {services.map((service) => {
              const Icon = service.icon;
              const isActive = activeService === service.id;

              return (
                <button
                  key={service.id}
                  onClick={() => setActiveService(service.id)}
                  className={`flex flex-col items-center justify-center min-h-[76px] px-1.5 py-2 rounded-xl border transition-all duration-300 ${
                    isActive
                      ? "bg-[#ee7d32] border-[#ee7d32]"
                      : "bg-white border-[#e5e7eb]"
                  }`}
                >
                  <Icon
                    className="w-5 h-5 mb-1.5"
                    style={{
                      stroke: isActive
                        ? "#ffffff"
                        : "#9ca3af",
                      fill: "none",
                    }}
                    strokeWidth={1.8}
                  />

                  <span
                    className={`text-[10px] font-medium text-center leading-tight transition-colors duration-300 ${
                      isActive
                        ? "text-white"
                        : "text-[#374151]"
                    }`}
                  >
                    {service.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        ref={serviceContentRef}
        className="min-h-screen scroll-mt-20"
      >
        {renderService()}
      </div>
    </section>
  );
};

export default Monitoring;